import { useEffect } from 'react';

const MIN_HEIGHT_PX = 200;

/**
 * Sizes the element in `ref` so its bottom edge sits just above the fixed
 * `.footer`, using the real layout instead of a hard-coded offset.
 *
 * height = visual viewport height - element top (document coordinates) - footer height
 *
 * While `enabled` is false, or when the hook unmounts, the inline height is
 * removed so the CSS fallback applies again.
 *
 * @param {React.RefObject<HTMLElement>} ref element to size
 * @param {boolean} enabled whether the hook should size the element
 */
export const useFillToFooterHeight = (ref, enabled = true) => {
    useEffect(() => {
        const element = ref.current;
        if (!enabled || !element) {
            return undefined;
        }

        let frameId = null;

        const measure = () => {
            frameId = null;
            const footer = document.querySelector('.footer');
            // Document coordinates, so the result does not change with scroll position
            const top = element.getBoundingClientRect().top + window.scrollY;
            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
            const height = Math.max(MIN_HEIGHT_PX, Math.floor(viewportHeight - top - footerHeight));
            element.style.height = `${height}px`;
        };

        // Coalesce bursts of resize events into one measurement per frame
        const scheduleMeasure = () => {
            if (frameId === null) {
                frameId = requestAnimationFrame(measure);
            }
        };

        const footer = document.querySelector('.footer');
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleMeasure) : null;
        // The parent holds the content above the element (categories, "List your business")
        if (element.parentElement) resizeObserver?.observe(element.parentElement);
        if (footer) resizeObserver?.observe(footer);

        window.addEventListener('resize', scheduleMeasure);
        window.visualViewport?.addEventListener('resize', scheduleMeasure);
        scheduleMeasure();

        return () => {
            if (frameId !== null) cancelAnimationFrame(frameId);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
            window.visualViewport?.removeEventListener('resize', scheduleMeasure);
            element.style.height = '';
        };
    }, [ref, enabled]);
};
